import { forwardRef, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";

export type ButtonGroupProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Visually joins the child buttons into a single connected control (a split
   * button): inner corners are squared and adjacent borders are collapsed.
   * Defaults to `true`. Set `false` for a spaced row of independent buttons.
   */
  attached?: boolean;
};

interface ButtonGroupOwnerState {
  attached: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities. The child
 * rules keep their original selector shape so specificity against the buttons'
 * own rules is unchanged (class + pseudo-class = 0,2,0). */

const groupBase: CSSObject = {
  display: "inline-flex",
  alignItems: "center"
};

const groupAttached: CSSObject = {
  "& > *:focus-visible": {
    position: "relative",
    zIndex: 10
  },
  "& > *:not(:first-child)": {
    marginLeft: "-1px",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0
  },
  "& > *:not(:last-child)": {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0
  }
};

const groupDetached: CSSObject = {
  gap: ".5rem"
};

const ButtonGroupRoot = styled("div", {
  label: "AgenticButtonGroup-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: ButtonGroupOwnerState }>(({ ownerState }) => [groupBase, ownerState.attached ? groupAttached : groupDetached]);

/**
 * Groups two or more `Button`/`IconButton` children into one connected control.
 * The canonical use is a split button — a primary action plus an adjacent
 * trigger (e.g. "Add Order" + a caret menu). The first child keeps its left
 * radius, the last child keeps its right radius, inner corners are squared, and
 * the 1px border overlap is collapsed so the segments read as one unit.
 */
export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className, attached = true, role = "group", children, ...props }, ref) => (
    <ButtonGroupRoot ref={ref} role={role} ownerState={{ attached }} className={className} {...props}>
      {children}
    </ButtonGroupRoot>
  )
);

ButtonGroup.displayName = "ButtonGroup";
